import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CONTROL_REGISTRY, resolveControlKey, isKnownControlKey } from '@/lib/controlRegistry';

export default function ControlDriftReport() {
  const isDev = import.meta.env.DEV;

  const logsQuery = useQuery({
    queryKey: ['control-drift-report'],
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('domain_logs')
        .select('control_key, created_at')
        .gte('created_at', since);
      if (error) throw error;
      return data || [];
    },
    enabled: isDev,
  });

  const report = useMemo(() => {
    const known = new Set(CONTROL_REGISTRY.map((entry) => entry.control_key));
    const seen = new Map<string, number>();
    const unknown = new Set<string>();
    (logsQuery.data || []).forEach((row) => {
      if (!row.control_key) return;
      const normalized = resolveControlKey(row.control_key);
      if (!isKnownControlKey(normalized)) {
        unknown.add(row.control_key);
        return;
      }
      seen.set(normalized, (seen.get(normalized) ?? 0) + 1);
    });

    const missing = Array.from(known).filter((key) => !seen.has(key));
    const duplicates = Array.from(seen.entries()).filter(([, count]) => count > 1);

    return { missing, unknown: Array.from(unknown), duplicates };
  }, [logsQuery.data]);

  if (!isDev) {
    return (
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Control drift report</CardTitle>
          <CardDescription>Available in development mode only.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Control drift report (last 24h)</CardTitle>
          <CardDescription>Detects unknown, missing, and duplicate control keys.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          {logsQuery.isLoading ? (
            <div>Loading…</div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">Unknown keys: {report.unknown.length}</Badge>
                <Badge variant="outline">Missing keys: {report.missing.length}</Badge>
                <Badge variant="outline">Duplicates: {report.duplicates.length}</Badge>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-foreground">Unknown keys seen</h3>
                {report.unknown.length === 0 ? (
                  <p className="text-xs text-muted-foreground">None detected.</p>
                ) : (
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
                    {report.unknown.map((key) => (
                      <li key={key}>{key}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-foreground">Known keys missing from logs</h3>
                {report.missing.length === 0 ? (
                  <p className="text-xs text-muted-foreground">None detected.</p>
                ) : (
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
                    {report.missing.map((key) => (
                      <li key={key}>{key}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-foreground">Duplicates detected</h3>
                {report.duplicates.length === 0 ? (
                  <p className="text-xs text-muted-foreground">None detected.</p>
                ) : (
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
                    {report.duplicates.map(([key, count]) => (
                      <li key={key}>
                        {key} ({count})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
